import { Chat } from "@/components/chat/chat";
import { DataStreamHandler } from "@/components/chat/data-stream-handler";
import { ErrorState, LoadingSkeleton } from "@/components/ui";
import { useGetChatById } from "@/hooks";
// import { getChatById, getMessagesByChatId } from "@/lib/db/queries";
// import type { DBMessage } from "@/lib/db/schema";
import type { Attachment, UIMessage } from "ai";
import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ChatLayout } from "./chat_layout";

export default function ChatById() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: chat,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetChatById(id!);

  const messages = chat?.messages || [];
  const type = chat?.type;

  useEffect(() => {
    if (type === "DASHBOARD" && !searchParams.get("type")) {
      setSearchParams({ type: "dashboard" });
    }
  }, [type, searchParams, setSearchParams]);
  if (isLoading) {
    return (
      <ChatLayout>
        <div className="h-full pb-2 flex items-center justify-center">
          <LoadingSkeleton type="header" />
        </div>
      </ChatLayout>
    );
  }

  if (isError) {
    // Check if it's a 404 error (chat not found)
    if ((error as any)?.statusCode === 404) {
      return (
        <ChatLayout>
          <div className="h-full pb-2 flex items-center justify-center">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Chat not found
              </h3>
              <p className="text-sm text-gray-500">
                The chat you're looking for doesn't exist or has been deleted.
              </p>
            </div>
          </div>
        </ChatLayout>
      );
    }

    return (
      <ChatLayout>
        <div className="h-full pb-2 flex items-center justify-center">
          <ErrorState
            error={error}
            onRetry={refetch}
            title="Failed to load chat"
          />
        </div>
      </ChatLayout>
    );
  }

  if (!chat) {
    return (
      <ChatLayout>
        <div className="h-full pb-2 flex items-center justify-center">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Chat not found
            </h3>
            <p className="text-sm text-gray-500">
              The chat you're looking for doesn't exist.
            </p>
          </div>
        </div>
      </ChatLayout>
    );
  }

  function convertToUIMessages(
    messages: Array<any> | undefined
  ): Array<UIMessage> {
    if (!messages || !Array.isArray(messages)) {
      return [];
    }
    return messages.map((message) => ({
      id: message.id,
      parts: message.parts as UIMessage["parts"],
      role: message.role as UIMessage["role"],
      // Note: content will soon be deprecated in @ai-sdk/react
      content: "",
      createdAt: message.createdAt,
      experimental_attachments:
        (message.attachments as Array<Attachment>) ?? [],
    }));
  }

  return (
    <ChatLayout>
      <Chat
        id={chat.id}
        initialMessages={convertToUIMessages(messages)}
        selectedChatModel={"chat-model"}
        selectedVisibilityType={chat.visibility}
        isReadonly={false}
        chatType={type}
      />
      <DataStreamHandler id={id!} />
    </ChatLayout>
  );
}
